import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Play, Pause, X, CheckCircle2, Volume2, RotateCcw, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import { useEmbeddedMode, postEmbeddedStepComplete } from '../hooks/useEmbeddedMode';

const HowOrbWorksPage = () => {
  const navigate = useNavigate();
  const { user, updateUser, refreshUser, api } = useAuth();
  const embedded = useEmbeddedMode();
  const videoRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [maxWatchedTime, setMaxWatchedTime] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  // iter166 — Mirror the pitch-video pattern: video element is
  // mounted muted so the browser autoplay policy lets it start
  // immediately. Once playback is running, a "Tap for sound"
  // overlay lets the user unmute inside a real click gesture.
  const [videoMuted, setVideoMuted] = useState(true);
  // iter166 — Timed gradient fade-in (identical to the pitch popup):
  // turns on when the video reaches 25s, then transitions over 15s
  // to a deep three-stop dark gradient behind the video. Adds the
  // same cinematic "settle into the content" effect.
  const [gradientOn, setGradientOn] = useState(false);

  const STEP2H_VIDEO_URL_DEFAULT = "/step2h_video.mp4";
  const [stepVideoUrl, setStepVideoUrl] = useState(STEP2H_VIDEO_URL_DEFAULT);

  // iter174 — Resolve the active step2h video from the admin override
  // stored in `system_settings.onboarding_videos` (PUT via the Admin
  // Onboarding Customisation page). Falls back to the static
  // `/step2h_video.mp4` only when no override has been set. This is
  // what unblocks production — the 65MB static file is too large to
  // be reliably served by the production CDN; admins now upload a
  // hosted URL via the admin panel and this page picks it up.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/me/onboarding/videos');
        const override = res?.data?.step2h_video?.url;
        if (alive && override) setStepVideoUrl(override);
      } catch (_e) { /* silent fallback to static */ }
    })();
    return () => { alive = false; };
  }, [api]);

  const STEP2H_VIDEO_URL = stepVideoUrl;

  // iter166 — Kick off muted autoplay as soon as the video element
  // mounts in the embedded popup. Muted-with-autoplay is universally
  // allowed by Chrome/Safari/iOS, so the video starts playing the
  // moment the popup opens — matching the "Watch the Pitch" UX.
  useEffect(() => {
    if (!embedded) return;
    const v = videoRef.current;
    if (!v || hasStarted) return;
    v.muted = true;
    setVideoMuted(true);
    const p = v.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => { /* autoplay blocked — overlay tap will start it */ });
    }
    setIsPlaying(true);
    setHasStarted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded]);

  // Check if already completed
  const hasCompleted = user?.step2h_how_orb_works;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartVideo = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
      setHasStarted(true);
    }
  };

  const handlePause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  // iter166 — Pitch-popup-style playback helpers (rewind/forward 10s,
  // playback-speed cycle, auto-fading controls).
  const [showControls, setShowControls] = useState(true);
  const [playbackRate, setPlaybackRate] = useState(1);
  const hideControlsTimer = useRef(null);
  const revealControls = () => {
    setShowControls(true);
    if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current);
    hideControlsTimer.current = setTimeout(() => setShowControls(false), 2500);
  };
  const skip = (delta) => {
    const v = videoRef.current;
    if (!v) return;
    try {
      const target = Math.max(0, Math.min((v.duration || 0) - 0.1, v.currentTime + delta));
      v.currentTime = target;
    } catch (_e) { /* noop */ }
  };
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      v.pause();
    }
  };
  const cycleSpeed = () => {
    const order = [1, 1.25, 1.5, 2, 0.75];
    const next = order[(order.indexOf(playbackRate) + 1) % order.length];
    setPlaybackRate(next);
    if (videoRef.current) videoRef.current.playbackRate = next;
  };

  const handleVideoEnd = async () => {
    setVideoEnded(true);
    setIsPlaying(false);
    
    // Mark step as complete
    try {
      await updateUser({ step2h_how_orb_works: true });
      await refreshUser();
      if (embedded) postEmbeddedStepComplete('step2h_how_orb_works');
    } catch (error) {
      console.error('Failed to update step:', error);
    }
  };

  const handleClose = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    if (!videoEnded) {
      toast.info('Complete the video to mark this step as done');
    }
    if (embedded) {
      // Let the parent popup decide what to do — typically it will
      // just dismiss the sheet on the next user interaction. We
      // can't navigate inside an iframe back to the parent route.
      return;
    }
    navigate('/');
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      setCurrentTime(video.currentTime);
      if (video.currentTime > maxWatchedTime) {
        setMaxWatchedTime(video.currentTime);
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeeking = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      if (video.currentTime > maxWatchedTime + 0.5) {
        video.currentTime = maxWatchedTime;
        toast.warning('Please watch the video without skipping');
      }
    }
  };

  const handleContinue = () => {
    navigate('/');
  };

  const remainingTime = Math.max(0, duration - currentTime);

  // If already completed, show success and redirect
  if (hasCompleted && !videoEnded) {
    return (
      <div className={embedded ? "min-h-screen w-full bg-black flex items-center justify-center" : "fixed inset-0 bg-black z-[100] flex items-center justify-center"}>
        <div className={embedded ? "w-full px-6" : "relative w-full max-w-md mx-4"}>
          <div className="bg-black/70 flex items-center justify-center rounded-lg p-8">
            <div className="text-center text-white">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Training Complete!</h3>
              <p className="text-gray-300 mb-4">You've already completed this training video.</p>
              {!embedded && (
                <Button onClick={handleContinue} className="bg-[#757150] hover:bg-[#8a8560]">
                  Return to Onboarding
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // iter166 — Embedded popup body matches the BusinessCard "Watch
  // the Pitch" popup EXACTLY (BusinessCardContent.js lines
  // 1370-1495):
  //   • Timed gradient overlay fades in over 15s when video hits 25s
  //   • Drag handle pill (rendered by the parent OnboardingStepSheet)
  //   • Video wrapper: w-[77%] mobile / md:h-full md:max-h-[72vh] desktop
  //   • Video element: block w-full h-auto md:w-auto md:h-full md:max-w-full md:object-contain
  //   • Speed cycle (top-left), playback controls (bottom center)
  //   • Tap-for-sound overlay until user unmutes
  //   • Drag-only dismiss (popup chrome hides the close X)
  if (embedded) {
    return (
      <div
        className="min-h-screen w-full bg-black flex flex-col relative overflow-hidden"
        data-testid="how-orb-works-page"
      >
        {/* Timed gradient fade-in overlay — same hex stops + 15s
            transition as the pitch popup. */}
        <div
          className="absolute inset-0 bg-gradient-to-b from-[#2a2a2a] via-[#111111] to-black pointer-events-none"
          style={{ opacity: gradientOn ? 1 : 0, transition: 'opacity 15000ms ease-out' }}
        />

        <div className="relative flex flex-col flex-1 min-h-0">
          <div className="flex-1 min-h-0 flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
            {/* Video wrapper. Exact pitch-popup classes. */}
            <div
              className="relative w-[77%] md:w-auto md:max-w-full md:h-full md:max-h-[72vh] rounded-2xl overflow-hidden mt-[5px]"
              style={{ aspectRatio: undefined }}
            >
              <video
                ref={videoRef}
                src={STEP2H_VIDEO_URL}
                preload="auto"
                playsInline
                autoPlay
                muted
                disablePictureInPicture
                controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onVolumeChange={(e) => setVideoMuted(!!e.currentTarget.muted)}
                onTimeUpdate={(e) => {
                  // Match pitch popup: at 25s, kick off the 15-second
                  // backdrop gradient fade.
                  if (!gradientOn && e.currentTarget.currentTime >= 25) setGradientOn(true);
                  handleTimeUpdate(e);
                }}
                onLoadedMetadata={handleLoadedMetadata}
                onSeeking={handleSeeking}
                onEnded={handleVideoEnd}
                onClick={(e) => { e.stopPropagation(); revealControls(); togglePlay(); }}
                className="block w-full h-auto md:w-auto md:h-full md:max-w-full md:object-contain cursor-pointer"
                data-testid="training-video"
              />

              {/* Tap-for-sound overlay (identical to the pitch popup). */}
              {videoMuted && !videoEnded && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const v = videoRef.current;
                    if (!v) return;
                    v.muted = false;
                    try {
                      const p = v.play();
                      if (p && typeof p.catch === 'function') p.catch(() => {});
                    } catch (_e) { /* noop */ }
                  }}
                  className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/45 backdrop-blur-[1px] text-white animate-in fade-in duration-200"
                  aria-label="Tap for sound"
                  data-testid="tap-for-sound"
                >
                  <div className="w-20 h-20 rounded-full bg-white/95 text-black flex items-center justify-center shadow-2xl">
                    <Volume2 className="w-9 h-9" />
                  </div>
                  <span className="text-sm font-semibold tracking-wide drop-shadow-md">Tap for sound</span>
                </button>
              )}

              {/* Playback speed — top-left, identical to pitch popup. */}
              <button
                onClick={(e) => { e.stopPropagation(); revealControls(); cycleSpeed(); }}
                className={`absolute top-3 left-3 h-9 min-w-[42px] px-2.5 rounded-full bg-black/55 backdrop-blur-sm hover:bg-black/70 flex items-center justify-center text-white text-xs font-bold transition-opacity duration-200 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                aria-label="Playback speed"
                data-testid="video-speed"
              >
                {playbackRate}x
              </button>

              {/* Playback controls — bottom center, identical to pitch popup. */}
              <div className={`absolute inset-x-0 bottom-3 flex items-center justify-center gap-5 px-4 transition-opacity duration-200 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                <button
                  onClick={(e) => { e.stopPropagation(); revealControls(); skip(-10); }}
                  className="h-10 w-10 rounded-full bg-black/55 backdrop-blur-sm hover:bg-black/70 flex items-center justify-center text-white active:scale-95 transition-all"
                  aria-label="Rewind 10 seconds"
                  data-testid="video-rewind"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>

                <button
                  onClick={(e) => { e.stopPropagation(); revealControls(); togglePlay(); }}
                  className="h-12 w-12 rounded-full bg-white/95 hover:bg-white flex items-center justify-center text-black active:scale-95 transition-all shadow-md"
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                  data-testid="video-playpause"
                >
                  {isPlaying ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5 ml-0.5" fill="currentColor" />}
                </button>

                <button
                  onClick={(e) => { e.stopPropagation(); revealControls(); skip(10); }}
                  className="h-10 w-10 rounded-full bg-black/55 backdrop-blur-sm hover:bg-black/70 flex items-center justify-center text-white active:scale-95 transition-all"
                  aria-label="Forward 10 seconds"
                  data-testid="video-forward"
                >
                  <RotateCw className="w-4 h-4" />
                </button>
              </div>

              {/* Success overlay when video ends — popup auto-closes
                  via postMessage fired in handleVideoEnd. */}
              {videoEnded && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center rounded-2xl">
                  <div className="text-center text-white px-6">
                    <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-2">Training Complete!</h3>
                    <p className="text-gray-300">Great job — closing this popup…</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black z-[100] flex items-center justify-center" data-testid="how-orb-works-page">
      <div className="relative w-full max-w-md mx-4">
        {/* Top Right Controls: Pause/Play and X (Close) - Only show after video started */}
        {hasStarted && (
          <div className="absolute top-4 right-4 z-10 flex gap-2">
            <Button
              variant="ghost"
              className="bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 h-auto"
              onClick={handlePause}
              data-testid="pause-play-btn"
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              className="bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 h-auto"
              onClick={handleClose}
              data-testid="close-btn"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}
        
        {/* Countdown Timer - Top Left - Only show when playing */}
        {hasStarted && duration > 0 && !videoEnded && (
          <div className="absolute top-4 left-4 z-10 bg-black/60 px-3 py-1 rounded-full">
            <span className="text-white text-sm font-medium">
              {formatTime(remainingTime)} remaining
            </span>
          </div>
        )}
        
        {/* Video Element */}
        <video
          ref={videoRef}
          className="w-full rounded-lg"
          onEnded={handleVideoEnd}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onSeeking={handleSeeking}
          playsInline
          data-testid="training-video"
        >
          <source src={STEP2H_VIDEO_URL} type="video/mp4" />
        </video>
        
        {/* Initial Play Overlay - Before video starts */}
        {!hasStarted && !videoEnded && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center rounded-lg">
            <div className="text-center text-white">
              <h3 className="text-xl font-bold mb-2">How does ORB Work?</h3>
              <p className="text-gray-300 mb-4 px-4">Watch this training video to continue with your onboarding.</p>
              <Button 
                onClick={handleStartVideo} 
                className="bg-[#757150] hover:bg-[#8a8560]"
                data-testid="start-video-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                Play Video
              </Button>
              <div className="mt-4">
                <Button
                  variant="ghost"
                  className="text-gray-400 hover:text-white"
                  onClick={handleClose}
                >
                  <X className="w-4 h-4 mr-2" />
                  Exit
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Success Overlay when video ends */}
        {videoEnded && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center rounded-lg">
            <div className="text-center text-white">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Training Complete!</h3>
              <p className="text-gray-300 mb-4">Great job! Continue with your onboarding.</p>
              <Button onClick={handleContinue} className="bg-[#757150] hover:bg-[#8a8560]">
                Continue
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HowOrbWorksPage;
